import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Save, X, Plus, Trash2, ChevronDown, ChevronUp, Star,
  Building2, User, Phone, MapPin, CreditCard, Wifi, Check,
  Home, Loader2, FileText, Upload, QrCode
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Property Types
const PROPERTY_TYPES = [
  'Apartment', 'Bed & Breakfast', 'Boutique Hotel', 'Camp', 'Cottage',
  'Farmhouse', 'Guest House', 'Home Stay', 'Hostel', 'Hotel',
  'Lodge', 'Resort', 'Service Apartment', 'Villa'
];

// Room Categories
const ROOM_CATEGORIES = ['Standard Room', 'Deluxe Room', 'Double Room', 'Single Room', 'Triple Room', 'Family Room', 'Suite', 'Other'];
const ROOM_TYPE_CATEGORIES = ['AC', 'Non-AC'];

// Auto-detect occupancy based on room type name
const detectOccupancy = (roomName) => {
  const name = (roomName || '').toLowerCase();
  if (name.includes('single')) return 1;
  if (name.includes('double')) return 2;
  if (name.includes('triple')) return 3;
  if (name.includes('family') || name.includes('quad')) return 4;
  if (name.includes('suite')) return 2;
  if (name.includes('deluxe')) return 2;
  if (name.includes('standard')) return 2;
  return 2;
};

// Indian States
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh'
];

// Room Amenities (Master Form Step 4 list — Bathroom has Private/Shared sub-choice)
const ROOM_AMENITIES = [
  'TV', 'Fan', 'Cooler', 'Wifi', 'Cupboard', 'Sofa',
  'Bathroom', 'Jacuzzi', 'Bathtub', 'Toiletries', 'Mirror'
];
const BATHROOM_OPTIONS = ['Private', 'Shared'];

// Property Facilities & Policies (Master Form Step 4 groups)
const YES_NO_FACILITIES = [
  'Couple Friendly', 'Unmarried Couples Allowed', 'Outside Food Allowed',
  'Non-Veg Food Allowed', 'Alcohol Allowed', 'Smoking Allowed Inside Room',
  'Local ID Allowed', 'Foreigners Allowed', 'Pet Allowed',
];
const CHARGE_FACILITIES = ['Parking', 'Room Heater', 'Breakfast', 'Swimming Pool'];
const TOGGLE_FACILITIES = [
  'Security Camera', 'Luggage Storage', 'Outdoor Dining Area', 'Restaurant',
  'Kitchen', '24 x 7 Reception', 'Room Service', 'Power Backup',
  'Banquet Hall', 'Conference Room',
];
const KNOWN_FACILITIES = new Set([...YES_NO_FACILITIES, ...CHARGE_FACILITIES, ...TOGGLE_FACILITIES]);

// Legacy camelCase key → display-label mapping for amenities
const AMENITY_KEY_MAP = {
  'wifi': 'Wifi', 'ac': 'Air Conditioning', 'reception': 'Reception', 'tv': 'TV',
  'roomService': 'Room Service', 'elevator': 'Elevator', 'powerBackup': 'Power Backup',
  'parking': 'Parking', 'breakfast': 'Breakfast', 'roomHeater': 'Room Heater',
  'smokingRoom': 'Smoking Room', 'fan': 'Fan', 'swimmingPool': 'Swimming Pool',
  'coupleFriendly': 'Couple Friendly', 'alcoholAllowed': 'Alcohol Allowed',
  'housekeeping': 'Housekeeping'
};

// Legacy camelCase key → display-label mapping for facilities
const FACILITY_KEY_MAP = {
  'securityCamera': 'Security Camera', 'luggageStorage': 'Luggage Storage',
  'banquet': 'Banquet', 'conferenceRoom': 'Conference Room', 'petsAllowed': 'Pets Allowed',
  'outdoorDining': 'Outdoor Dining Area', 'outsideFoodAllowed': 'Outside Food Allowed',
  'noSmokingInside': 'No Smoking Inside', 'unmarriedCouplesAllowed': 'Unmarried Couples Allowed',
  'onlyMalesGroupAllowed': 'Only Males Group Allowed', 'nonVegFoodAllowed': 'Non Veg Food Allowed',
  'restaurant': 'Restaurant', 'kitchen': 'Kitchen', 'wheelchairAccess': 'WheelChair Access'
};

// Normalize amenities/facilities: convert camelCase keys to display labels, strip falsy values
const normalizeToggleData = (raw, keyMap) => {
  if (!raw || typeof raw !== 'object') return {};
  const normalized = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!value || value === false) continue; // Skip unchecked items
    const displayKey = keyMap[key] || key; // Convert legacy key or keep if already display label
    normalized[displayKey] = typeof value === 'object' ? value : { type: 'free' };
  }
  return normalized;
};

// Item 2 — render every saved facility attribute in the Property Details view:
// Yes/No, Indoor/Outdoor (pool), Free/Paid + amount. Supports both the Master
// Form shape ({ value, charge, amount, poolType }) and the legacy
// ({ type, price }) shape so nothing is ever hidden.
// FREE/PAID badges are shown ONLY for facilities that actually support the
// Chargeable option (Parking, Room Heater, Breakfast, Swimming Pool) or
// Pet Allowed. Yes/No-only policies never show FREE/PAID even if legacy data
// was stored with a `type:'free'` shape.
const CHARGE_SUPPORTED = new Set(['Parking', 'Room Heater', 'Breakfast', 'Swimming Pool', 'Pet Allowed']);
const FacilityBadges = ({ config, name }) => {
  if (!config || typeof config !== 'object') return null;
  const { value, charge, amount, poolType, type, price } = config;
  const supportsCharge = CHARGE_SUPPORTED.has(name);
  const isFree = supportsCharge && (charge === 'free' || type === 'free');
  const isPaid = supportsCharge && (charge === 'paid' || type === 'paid');
  const amt = amount ?? price;
  if (!value && !poolType && !isFree && !isPaid) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5 text-xs" data-testid="facility-badges">
      {value && (
        <span className={`px-2 py-0.5 rounded font-medium ${value === 'Yes' ? 'bg-teal-100 text-teal-700' : 'bg-gray-200 text-gray-600'}`}>{value}</span>
      )}
      {poolType && <span className="px-2 py-0.5 rounded bg-sky-100 text-sky-700 font-medium">{poolType}</span>}
      {isFree && <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded">FREE</span>}
      {isPaid && <span className="px-2 py-0.5 bg-gray-800 text-white rounded">₹{amt || 0}</span>}
    </div>
  );
};

// Section Component
const FormSection = ({ title, icon: Icon, children, defaultOpen = true, color = "teal" }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-5 py-4 bg-gradient-to-r from-${color}-50 to-white hover:from-${color}-100 transition-all`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg bg-${color}-100 flex items-center justify-center`}>
            <Icon size={18} className={`text-${color}-600`} />
          </div>
          <span className="font-semibold text-gray-800">{title}</span>
        </div>
        {isOpen ? <ChevronUp size={20} className="text-gray-500" /> : <ChevronDown size={20} className="text-gray-500" />}
      </button>
      {isOpen && <div className="p-5 border-t border-gray-100">{children}</div>}
    </div>
  );
};

// Input Field
const InputField = ({ label, required, disabled, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      {...props}
      disabled={disabled}
      className={`w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none transition-all ${
        disabled ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : 'bg-white'
      }`}
    />
  </div>
);

// Select Field
const SelectField = ({ label, required, disabled, options, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      {...props}
      disabled={disabled}
      className={`w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none transition-all ${
        disabled ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : 'bg-white'
      }`}
    >
      <option value="">Select...</option>
      {options.map(opt => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  </div>
);

// View Field (Read-only)
const ViewField = ({ label, value }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    <p className="text-sm text-gray-900 py-2 px-3 bg-gray-50 rounded-lg min-h-[40px]">
      {value || <span className="text-gray-400">—</span>}
    </p>
  </div>
);

// Star Rating Component
const StarRating = ({ value, onChange, disabled }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map(star => (
      <button
        key={star}
        type="button"
        disabled={disabled}
        onClick={() => onChange?.(star)}
        className={`p-1 transition-all ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-110'}`}
      >
        <Star
          size={28}
          className={`${star <= value ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'} transition-colors`}
        />
      </button>
    ))}
  </div>
);

// Amenity Checkbox Card
const AmenityCard = ({ label, isSelected, onToggle, config, onConfigChange, disabled }) => {
  return (
    <div
      className={`px-4 py-3 rounded-xl border-2 transition-all ${
        isSelected 
          ? 'border-teal-500 bg-teal-50/30' 
          : 'border-gray-100 hover:border-gray-200'
      } ${disabled ? 'opacity-60' : 'cursor-pointer'}`}
      onClick={() => !disabled && onToggle()}
    >
      <div className="flex items-center gap-3">
        <div className={`w-5 h-5 rounded flex items-center justify-center ${
          isSelected ? 'bg-teal-500 text-white' : 'bg-gray-100'
        }`}>
          {isSelected && <Check size={14} strokeWidth={3} />}
        </div>
        <span className={`text-sm font-medium ${isSelected ? 'text-gray-900' : 'text-gray-600'}`}>
          {label}
        </span>
      </div>
      
      {isSelected && !disabled && (
        <div className="mt-3 flex gap-2" onClick={e => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => onConfigChange?.({ type: 'free' })}
            className={`flex-1 py-1.5 text-xs font-bold rounded-lg ${
              config?.type === 'free' ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            FREE
          </button>
          <button
            type="button"
            onClick={() => onConfigChange?.({ type: 'paid', price: config?.price || 0 })}
            className={`flex-1 py-1.5 text-xs font-bold rounded-lg ${
              config?.type === 'paid' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            PAID
          </button>
        </div>
      )}
      
      {isSelected && config?.type === 'paid' && !disabled && (
        <div className="mt-2" onClick={e => e.stopPropagation()}>
          <input
            type="number"
            placeholder="Price ₹"
            value={config?.price || ''}
            onChange={(e) => onConfigChange?.({ type: 'paid', price: parseFloat(e.target.value) || 0 })}
            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
          />
        </div>
      )}
    </div>
  );
};

// Default empty form data
const getEmptyFormData = () => ({
  propertyName: '',
  propertyType: 'Hotel',
  starRating: 3,
  totalRooms: '',
  address: '',
  state: '',
  city: '',
  pinCode: '',
  googleMapLink: '',
  description: '',
  ownerName: '',
  mobile: '',
  email: '',
  altContact: '',
  rooms: [],
  amenities: {},
  facilities: {},
  other_facilities: '',
  banking: {
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    gstNumber: ''
  },
  propertyStatus: 'Active',
  onboardingStatus: 'In Progress'
});

/**
 * HotelOnboardingForm - Onboarding form for properties
 * Based on uploaded hotel-onboarding-form.zip structure
 */
const HotelOnboardingForm = ({
  mode = 'create',
  initialData = null,
  onSave,
  onCancel,
  onModeChange,
  saving = false,
  isPublic = false
}) => {
  const [formData, setFormData] = useState(getEmptyFormData());
  const isDisabled = mode === 'view';
  const { user } = useAuth();
  // Item 5 — gtm_sales & gtm_sales_leader are view-only on Properties.
  const restrictedFromEdit = ['gtm_sales', 'gtm_sales_leader'].includes((user?.role || '').toLowerCase());

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...getEmptyFormData(),
        ...initialData,
        amenities: normalizeToggleData(initialData.amenities, AMENITY_KEY_MAP),
        facilities: normalizeToggleData(initialData.facilities, FACILITY_KEY_MAP),
        banking: {
          ...getEmptyFormData().banking,
          ...(initialData.banking || {})
        }
      });
    } else {
      setFormData(getEmptyFormData());
    }
  }, [initialData, mode]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBankingChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      banking: { ...prev.banking, [field]: value }
    }));
  };

  // Cancelled Cheque / QR upload — identical logic to the Add-Property
  // (MultiStepOnboardingForm) flow. Uses the same public master-form
  // staging endpoint; only the UI is exposed here in Edit mode so
  // existing files can be viewed / replaced / newly uploaded.
  const [bankUploading, setBankUploading] = useState({ cheque: false, qr: false });
  const handleBankDocUpload = async (docType, file) => {
    if (!file) return;
    const isQr = docType === 'qr';
    const allowed = isQr ? ['.jpg', '.jpeg', '.png', '.webp'] : ['.pdf', '.jpg', '.jpeg', '.png', '.webp'];
    const lower = file.name.toLowerCase();
    if (!allowed.some(ext => lower.endsWith(ext))) {
      alert(`Unsupported file type. Allowed: ${allowed.join(', ')}`);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('File size exceeds 10MB limit');
      return;
    }
    try {
      setBankUploading(prev => ({ ...prev, [docType]: true }));
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const res = await fetch(`${API_BASE}/api/master-form/upload?doc_type=${docType}`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Upload failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      if (isQr) {
        handleBankingChange('qrCodeUrl', data.url);
        handleBankingChange('qrCodeName', data.name);
      } else {
        handleBankingChange('cancelledChequeUrl', data.url);
        handleBankingChange('cancelledChequeName', data.name);
      }
    } catch (e) {
      alert(e.message || 'Upload failed');
    } finally {
      setBankUploading(prev => ({ ...prev, [docType]: false }));
    }
  };

  // Room Management
  const addRoom = () => {
    const defaultCategory = 'Standard Room';
    setFormData(prev => ({
      ...prev,
      rooms: [...prev.rooms, { id: crypto.randomUUID(), category: defaultCategory, roomTypeCategory: 'AC', count: 1, basePrice: 0, customRoomType: '', occupancy: detectOccupancy(defaultCategory) }]
    }));
  };

  const updateRoom = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      rooms: prev.rooms.map(r => {
        if (r.id !== id) return r;
        const updated = { ...r, [field]: value };
        if (field === 'category') {
          const roomName = value === 'Other' ? r.customRoomType : value;
          updated.occupancy = detectOccupancy(roomName);
        }
        if (field === 'customRoomType' && r.category === 'Other') {
          updated.occupancy = detectOccupancy(value);
        }
        return updated;
      })
    }));
  };

  const removeRoom = (id) => {
    setFormData(prev => ({
      ...prev,
      rooms: prev.rooms.filter(r => r.id !== id)
    }));
  };

  // Amenity Management
  const toggleAmenity = (type, key) => {
    setFormData(prev => {
      const current = { ...prev[type] };
      if (current[key]) {
        delete current[key];
      } else {
        current[key] = { type: 'free' };
      }
      return { ...prev, [type]: current };
    });
  };

  const updateAmenityConfig = (type, key, config) => {
    setFormData(prev => ({
      ...prev,
      [type]: { ...prev[type], [key]: config }
    }));
  };

  // Per-room (room-type specific) amenities — mirrors Master Form Step 4.
  const toggleRoomAmenity = (roomId, amenity) => {
    setFormData(prev => ({
      ...prev,
      rooms: prev.rooms.map(r => {
        if (r.id !== roomId) return r;
        const current = { ...(r.amenities || {}) };
        if (current[amenity]) {
          delete current[amenity];
        } else {
          current[amenity] = amenity === 'Bathroom' ? { variant: '' } : true;
        }
        return { ...r, amenities: current };
      })
    }));
  };

  const setRoomBathroomVariant = (roomId, variant) => {
    setFormData(prev => ({
      ...prev,
      rooms: prev.rooms.map(r => {
        if (r.id !== roomId) return r;
        return { ...r, amenities: { ...(r.amenities || {}), Bathroom: { variant } } };
      })
    }));
  };

  // Facility helpers — Master Form value shapes ({value}, {charge, amount}, {selected}).
  const toggleFacility = (key, defaultVal) => {
    setFormData(prev => {
      const current = { ...(prev.facilities || {}) };
      if (current[key]) {
        delete current[key];
      } else {
        current[key] = defaultVal;
      }
      return { ...prev, facilities: current };
    });
  };

  const setFacilityValue = (key, val) => {
    setFormData(prev => ({
      ...prev,
      facilities: { ...(prev.facilities || {}), [key]: { ...(prev.facilities?.[key] || {}), ...val } }
    }));
  };

  const setYesNoFacility = (key, value) => {
    setFormData(prev => ({
      ...prev,
      facilities: { ...(prev.facilities || {}), [key]: { ...(prev.facilities?.[key] || {}), value } }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const numOrNull = (v) => (v === '' || v === undefined || v === null ? null : Number(v));
    const payload = {
      ...formData,
      rooms: (formData.rooms || []).map(r => ({
        ...r,
        weekendPrice: numOrNull(r.weekendPrice),
        extraAdultPrice: numOrNull(r.extraAdultPrice),
      })),
    };
    onSave?.(payload);
  };

  // Render field based on mode
  const renderField = (viewLabel, editComponent, value) => {
    if (mode === 'view') {
      return <ViewField label={viewLabel} value={value} />;
    }
    return editComponent;
  };

  // ---- Room amenity chip (edit) — mirrors Master Form Step 4 ----
  const renderRoomAmenityChip = (room, amenity) => {
    const selected = !!room.amenities?.[amenity];
    const isBathroom = amenity === 'Bathroom';
    const bathVariant = isBathroom && room.amenities?.Bathroom?.variant;
    return (
      <div
        key={amenity}
        onClick={() => toggleRoomAmenity(room.id, amenity)}
        className={`px-3 py-2.5 rounded-lg border-2 cursor-pointer transition-all ${selected ? 'border-teal-500 bg-teal-50/40' : 'border-gray-100 hover:border-gray-200'}`}
        data-testid={`edit-room-amenity-${room.id}-${amenity.toLowerCase()}`}
      >
        <div className="flex items-center gap-2">
          <div className={`w-4 h-4 rounded flex items-center justify-center ${selected ? 'bg-teal-500 text-white' : 'bg-gray-100'}`}>
            {selected && <Check size={11} strokeWidth={3} />}
          </div>
          <span className={`text-sm font-medium ${selected ? 'text-gray-900' : 'text-gray-600'}`}>{amenity}</span>
        </div>
        {isBathroom && selected && (
          <div className="mt-2 flex gap-1.5" onClick={(e) => e.stopPropagation()}>
            {BATHROOM_OPTIONS.map(opt => (
              <button key={opt} type="button" onClick={() => setRoomBathroomVariant(room.id, opt)}
                className={`flex-1 py-1 text-xs font-bold rounded-md ${bathVariant === opt ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                data-testid={`edit-room-bathroom-${room.id}-${opt.toLowerCase()}`}
              >{opt}</button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ---- Facility renderers (edit) — mirror Master Form Step 4 groups ----
  const renderYesNoFacility = (name) => {
    const f = formData.facilities?.[name];
    const val = f?.value;
    const isPet = name === 'Pet Allowed';
    const petCharge = isPet ? (f?.charge || f?.type) : null;
    const petAmount = isPet ? (f?.amount ?? f?.price) : null;
    const tid = `edit-facility-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    return (
      <div key={name} className={`px-4 py-3 rounded-xl border-2 transition-all ${val ? 'border-teal-500 bg-teal-50/30' : 'border-gray-100'}`} data-testid={tid}>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-medium text-gray-700">{name}</span>
          <div className="flex gap-1.5">
            {['Yes', 'No'].map(opt => (
              <button key={opt} type="button" onClick={() => setYesNoFacility(name, opt)}
                className={`px-3 py-1 text-xs font-bold rounded-lg ${val === opt ? (opt === 'Yes' ? 'bg-teal-600 text-white' : 'bg-gray-700 text-white') : 'bg-gray-100 text-gray-600'}`}
                data-testid={`${tid}-${opt.toLowerCase()}`}
              >{opt}</button>
            ))}
          </div>
        </div>
        {isPet && val === 'Yes' && (
          <div className="mt-3 space-y-2">
            <div className="flex gap-2">
              {['free', 'paid'].map(t => (
                <button key={t} type="button"
                  onClick={() => setFacilityValue(name, { charge: t, type: t, ...(t === 'free' ? { amount: '', price: '' } : {}) })}
                  className={`flex-1 py-1.5 text-xs font-bold rounded-lg uppercase ${petCharge === t ? (t === 'free' ? 'bg-teal-500 text-white' : 'bg-gray-800 text-white') : 'bg-gray-100 text-gray-600'}`}
                  data-testid={`${tid}-${t}`}
                >{t}</button>
              ))}
            </div>
            {petCharge === 'paid' && (
              <input type="number" min={0} placeholder="Charge ₹" value={petAmount ?? ''}
                onChange={(e) => setFacilityValue(name, { amount: e.target.value, price: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
                data-testid={`${tid}-amount`} />
            )}
          </div>
        )}
      </div>
    );
  };

  const renderToggleFacility = (name) => {
    const on = !!formData.facilities?.[name];
    const tid = `edit-facility-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    return (
      <div key={name} onClick={() => toggleFacility(name, { selected: true })}
        className={`px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${on ? 'border-teal-500 bg-teal-50/40' : 'border-gray-100 hover:border-gray-200'}`}
        data-testid={tid}
      >
        <div className="flex items-center gap-3">
          <div className={`w-5 h-5 rounded flex items-center justify-center ${on ? 'bg-teal-500 text-white' : 'bg-gray-100'}`}>
            {on && <Check size={14} strokeWidth={3} />}
          </div>
          <span className={`text-sm font-medium ${on ? 'text-gray-900' : 'text-gray-600'}`}>{name}</span>
        </div>
      </div>
    );
  };

  // Legacy/unknown facility keys stored on older properties (not part of the
  // configured YES_NO / CHARGE / TOGGLE groups). Render as plain Yes/No — no
  // Free/Paid controls — and preserve existing saved value if present.
  const renderLegacyYesNoFacility = (name) => {
    const f = formData.facilities?.[name];
    // Preserve existing saved value; if legacy data was just truthy (e.g. {type:'free'} or true)
    // we surface it as "Yes" so the user visibly sees the previously saved on-state.
    const val = f?.value || (f ? 'Yes' : null);
    const tid = `edit-facility-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    return (
      <div key={name} className={`px-4 py-3 rounded-xl border-2 transition-all ${val ? 'border-teal-500 bg-teal-50/30' : 'border-gray-100'}`} data-testid={tid}>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-medium text-gray-700">{name}</span>
          <div className="flex gap-1.5">
            {['Yes', 'No'].map(opt => (
              <button key={opt} type="button" onClick={() => setYesNoFacility(name, opt)}
                className={`px-3 py-1 text-xs font-bold rounded-lg ${val === opt ? (opt === 'Yes' ? 'bg-teal-600 text-white' : 'bg-gray-700 text-white') : 'bg-gray-100 text-gray-600'}`}
                data-testid={`${tid}-${opt.toLowerCase()}`}
              >{opt}</button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderChargeFacility = (name) => {
    const f = formData.facilities?.[name];
    const on = !!f;
    const charge = f?.charge || f?.type;
    const amount = f?.amount ?? f?.price;
    const isPool = name === 'Swimming Pool';
    const tid = `edit-facility-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    return (
      <div key={name} onClick={() => toggleFacility(name, isPool ? { charge: '', poolType: '' } : { charge: '' })}
        className={`px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${on ? 'border-teal-500 bg-teal-50/40' : 'border-gray-100 hover:border-gray-200'}`}
        data-testid={tid}
      >
        <div className="flex items-center gap-3">
          <div className={`w-5 h-5 rounded flex items-center justify-center ${on ? 'bg-teal-500 text-white' : 'bg-gray-100'}`}>
            {on && <Check size={14} strokeWidth={3} />}
          </div>
          <span className={`text-sm font-medium ${on ? 'text-gray-900' : 'text-gray-600'}`}>{name}</span>
        </div>
        {on && (
          <div className="mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex gap-2">
              {['free', 'paid'].map(t => (
                <button key={t} type="button"
                  onClick={() => setFacilityValue(name, { charge: t, type: t, ...(t === 'free' ? { amount: '', price: '' } : {}) })}
                  className={`flex-1 py-1.5 text-xs font-bold rounded-lg uppercase ${charge === t ? (t === 'free' ? 'bg-teal-500 text-white' : 'bg-gray-800 text-white') : 'bg-gray-100 text-gray-600'}`}
                  data-testid={`${tid}-${t}`}
                >{t}</button>
              ))}
            </div>
            {charge === 'paid' && (
              <input type="number" min={0} placeholder="Charge ₹" value={amount ?? ''}
                onChange={(e) => setFacilityValue(name, { amount: e.target.value, price: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
                data-testid={`${tid}-amount`} />
            )}
            {isPool && (
              <div className="flex gap-2 pt-1">
                {['Indoor', 'Outdoor'].map(v => (
                  <button key={v} type="button" onClick={() => setFacilityValue('Swimming Pool', { poolType: v })}
                    className={`flex-1 py-1.5 text-xs font-bold rounded-lg ${f.poolType === v ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                    data-testid={`edit-facility-pool-${v.toLowerCase()}`}
                  >{v}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Legacy / extra facility keys saved on the property but not in the Master Form groups.
  const extraFacilityKeys = Object.keys(formData.facilities || {}).filter(k => !KNOWN_FACILITIES.has(k));

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="hotel-onboarding-form">
      {/* 1. Property Basics */}
      <FormSection title="Property Basics" icon={Building2} defaultOpen={true} color="teal">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {renderField(
            'Property Name',
            <InputField
              label="Property Name"
              required
              value={formData.propertyName}
              onChange={(e) => handleChange('propertyName', e.target.value)}
              placeholder="e.g. Grand Horizon Hotel"
              disabled={isDisabled}
            />,
            formData.propertyName
          )}
          
          {renderField(
            'Property Type',
            <SelectField
              label="Property Type"
              required
              value={formData.propertyType}
              onChange={(e) => handleChange('propertyType', e.target.value)}
              options={PROPERTY_TYPES}
              disabled={isDisabled}
            />,
            formData.propertyType
          )}
        </div>

        <div className="mt-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">Star Rating</label>
          {mode === 'view' ? (
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map(star => (
                <Star
                  key={star}
                  size={24}
                  className={star <= formData.starRating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}
                />
              ))}
            </div>
          ) : (
            <StarRating
              value={formData.starRating}
              onChange={(val) => handleChange('starRating', val)}
              disabled={isDisabled}
            />
          )}
        </div>

        {/* Total Number of Rooms */}
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          {renderField(
            'Total Number of Rooms',
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Total Number of Rooms <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.totalRooms || ''}
                onChange={(e) => handleChange('totalRooms', e.target.value)}
                placeholder="e.g. 50"
                min="1"
                max="1000"
                disabled={isDisabled}
                className={`w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none ${
                  isDisabled ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : 'bg-white'
                }`}
                data-testid="total-rooms-input"
              />
            </div>,
            formData.totalRooms ? `${formData.totalRooms} Rooms` : null
          )}
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          {renderField(
            'Full Address',
            <InputField
              label="Full Address"
              required
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder="Street, Area"
              disabled={isDisabled}
            />,
            formData.address
          )}          
          {renderField(
            'State',
            <SelectField
              label="State"
              required
              value={formData.state}
              onChange={(e) => handleChange('state', e.target.value)}
              options={INDIAN_STATES}
              disabled={isDisabled}
            />,
            formData.state
          )}
          
          {renderField(
            'City',
            <InputField
              label="City"
              required
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="City name"
              disabled={isDisabled}
            />,
            formData.city
          )}
          
          {renderField(
            'Pin Code',
            <InputField
              label="Pin Code"
              required
              value={formData.pinCode}
              onChange={(e) => handleChange('pinCode', e.target.value)}
              placeholder="6-digit PIN"
              maxLength={6}
              disabled={isDisabled}
            />,
            formData.pinCode
          )}
        </div>

        <div className="mt-5">
          {mode === 'view' ? (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Google Map Link</label>
              <p className="text-sm py-2 px-3 bg-gray-50 rounded-lg min-h-[40px] break-all">
                {formData.googleMapLink ? (
                  <a
                    href={formData.googleMapLink}
                    target="_blank"
                    rel="noreferrer"
                    className="text-teal-600 hover:underline"
                    data-testid="property-google-map-link"
                  >
                    {formData.googleMapLink}
                  </a>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </p>
            </div>
          ) : (
            <InputField
              label="Google Map Link"
              value={formData.googleMapLink}
              onChange={(e) => handleChange('googleMapLink', e.target.value)}
              placeholder="https://maps.google.com/..."
              type="url"
              disabled={isDisabled}
            />
          )}
        </div>

        <div className="mt-5">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Property Description</label>
          {mode === 'view' ? (
            <p className="text-sm text-gray-900 py-2 px-3 bg-gray-50 rounded-lg min-h-[80px]">
              {formData.description || <span className="text-gray-400">No description provided</span>}
            </p>
          ) : (
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe your property..."
              rows={3}
              disabled={isDisabled}
              className={`w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none resize-none ${
                isDisabled ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : 'bg-white'
              }`}
            />
          )}
        </div>
      </FormSection>

      {/* 2. Contact Info */}
      <FormSection title="Contact Information" icon={User} defaultOpen={true} color="blue">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {renderField(
            'Owner / Manager Name',
            <InputField
              label="Owner / Manager Name"
              required
              value={formData.ownerName}
              onChange={(e) => handleChange('ownerName', e.target.value)}
              placeholder="Full Name"
              disabled={isDisabled}
            />,
            formData.ownerName
          )}
          
          {renderField(
            'Official Email',
            <InputField
              label="Official Email"
              required
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="admin@hotel.com"
              disabled={isDisabled}
            />,
            formData.email
          )}
          
          {renderField(
            'Mobile Number',
            <InputField
              label="Mobile Number"
              required
              type="tel"
              value={formData.mobile}
              onChange={(e) => handleChange('mobile', e.target.value)}
              placeholder="+91 9876543210"
              disabled={isDisabled}
            />,
            formData.mobile
          )}
          
          {renderField(
            'Alternative Contact',
            <InputField
              label="Alternative Contact"
              type="tel"
              value={formData.altContact}
              onChange={(e) => handleChange('altContact', e.target.value)}
              placeholder="Landline or Secondary Mobile"
              disabled={isDisabled}
            />,
            formData.altContact
          )}
        </div>
      </FormSection>

      {/* 3. Inventory (Room Configuration) */}
      <FormSection title="Room Inventory" icon={Home} defaultOpen={mode !== 'view'} color="purple">
        {mode !== 'view' && (
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">Add room categories and inventory details</p>
            <button
              type="button"
              onClick={addRoom}
              className="flex items-center px-4 py-2 text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-50 transition-colors font-medium text-sm"
              data-testid="add-room-btn"
            >
              <Plus size={18} className="mr-2" /> Add Room Category
            </button>
          </div>
        )}

        {/* Total Rooms Validation */}
        {(() => {
          const totalRoomsNum = parseInt(formData.totalRooms) || 0;
          const inventorySum = formData.rooms.reduce((sum, r) => sum + (r.count || 0), 0);
          const isOverLimit = totalRoomsNum > 0 && inventorySum > totalRoomsNum;
          return totalRoomsNum > 0 && formData.rooms.length > 0 ? (
            <div className={`flex items-center justify-between px-4 py-3 rounded-xl border text-sm font-medium mb-4 ${
              isOverLimit ? 'bg-red-50 border-red-200 text-red-700' : 'bg-teal-50 border-teal-200 text-teal-700'
            }`} data-testid="room-validation-banner">
              <span>Room Inventory: <strong>{inventorySum}</strong> / {totalRoomsNum} total rooms</span>
              {isOverLimit && <span className="text-red-600 text-xs">Exceeds total rooms limit!</span>}
            </div>
          ) : null;
        })()}

        {formData.rooms.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <p className="text-gray-500">No rooms configured yet</p>
            {mode !== 'view' && (
              <button
                type="button"
                onClick={addRoom}
                className="mt-3 text-teal-600 hover:text-teal-700 font-medium text-sm"
              >
                Add your first room category
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className={mode === 'view' ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'grid grid-cols-1 md:grid-cols-2 gap-4'}>
              {formData.rooms.map((room, index) => {
                const roomLabel = room.category === 'Other' ? (room.customRoomType || 'Custom Room') : room.category;
                const occ = room.occupancy || detectOccupancy(roomLabel);
                return (
                  <div key={room.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid={`room-card-${index}`}>
                    {/* Card Header */}
                    <div className="px-5 py-3 bg-gradient-to-r from-slate-800 to-slate-700 flex items-center justify-between">
                      <h4 className="text-white font-semibold text-sm">{roomLabel}</h4>
                      {mode !== 'view' && (
                        <button type="button" onClick={() => removeRoom(room.id)} className="p-1 text-slate-300 hover:text-red-300 rounded transition-colors">
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>

                    <div className="p-5 space-y-4">
                      {mode === 'view' ? (
                        <>
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              (room.roomTypeCategory || 'AC') === 'AC' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                            }`}>{room.roomTypeCategory || 'AC'}</span>
                          </div>
                          <div className="grid grid-cols-3 gap-3 text-sm">
                            <div>
                              <span className="text-xs text-gray-500 block">No of Rooms</span>
                              <span className="font-semibold text-gray-800">{room.count || 0}</span>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 block">Size</span>
                              <span className="font-semibold text-gray-800">{room.roomSize ? `${room.roomSize} ${room.roomSizeUnit || 'sq ft'}` : '—'}</span>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 block">Occupancy</span>
                              <span className="font-semibold text-gray-800">{occ} {occ === 1 ? 'Guest' : 'Guests'}</span>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 block">Adults</span>
                              <span className="font-semibold text-gray-800">{(room.noOfAdults ?? '') === '' ? '—' : room.noOfAdults}</span>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 block">Children</span>
                              <span className="font-semibold text-gray-800">{(room.noOfChildren ?? '') === '' ? '—' : room.noOfChildren}</span>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 block">Base Price</span>
                              <span className="font-semibold text-gray-800">&#8377;{room.basePrice || 0}/night</span>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 block">Weekend Price</span>
                              <span className="font-semibold text-gray-800">{room.weekendPrice ? `\u20B9${room.weekendPrice}/night` : '—'}</span>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500 block">Extra Adult Price</span>
                              <span className="font-semibold text-gray-800">{room.extraAdultPrice ? `\u20B9${room.extraAdultPrice}` : '—'}</span>
                            </div>
                          </div>
                          {room.amenities && Object.keys(room.amenities).length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <span className="text-xs text-gray-500 block mb-1.5">Amenities</span>
                              <div className="flex flex-wrap gap-1.5">
                                {Object.entries(room.amenities).map(([a, v]) => {
                                  const label = (a === 'Bathroom' && v?.variant) ? `Bathroom (${v.variant})` : a;
                                  return <span key={a} className="px-2 py-0.5 bg-teal-50 text-teal-700 text-xs rounded-full">{label}</span>;
                                })}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {/* Room Type Select */}
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Room Type</label>
                            <select value={room.category} onChange={(e) => { updateRoom(room.id, 'category', e.target.value); if (e.target.value !== 'Other') updateRoom(room.id, 'customRoomType', ''); }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none bg-white">
                              {ROOM_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          {room.category === 'Other' && (
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Custom Name</label>
                              <input type="text" value={room.customRoomType || ''} onChange={(e) => updateRoom(room.id, 'customRoomType', e.target.value)} placeholder="e.g. Executive Room"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none" />
                            </div>
                          )}
                          {/* AC Toggle */}
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">AC Status</label>
                            <div className="flex gap-2">
                              {ROOM_TYPE_CATEGORIES.map(type => (
                                <button key={type} type="button" onClick={() => updateRoom(room.id, 'roomTypeCategory', type)}
                                  className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
                                    (room.roomTypeCategory || 'AC') === type
                                      ? type === 'AC' ? 'bg-blue-600 text-white shadow-sm' : 'bg-orange-500 text-white shadow-sm'
                                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                  }`}>
                                  {type}
                                </button>
                              ))}
                            </div>
                          </div>
                          {/* Rooms & Room Size */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Rooms</label>
                              <input type="number" min={1} value={room.count} onChange={(e) => updateRoom(room.id, 'count', parseInt(e.target.value) || 0)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none" data-testid={`edit-room-count-${index}`} />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Room Size</label>
                              <div className="flex gap-2">
                                <input type="number" min={1} value={room.roomSize ?? ''} onChange={(e) => updateRoom(room.id, 'roomSize', e.target.value)} placeholder="e.g. 150"
                                  className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none" data-testid={`edit-room-size-${index}`} />
                                <select value={room.roomSizeUnit || 'sq ft'} onChange={(e) => updateRoom(room.id, 'roomSizeUnit', e.target.value)}
                                  className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none bg-white" data-testid={`edit-room-size-unit-${index}`}>
                                  <option value="sq ft">sq ft</option>
                                  <option value="sq m">sq m</option>
                                </select>
                              </div>
                            </div>
                          </div>
                          {/* Base & Weekend Price */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Base Price / Night</label>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">&#8377;</span>
                                <input type="number" min={0} value={room.basePrice} onChange={(e) => updateRoom(room.id, 'basePrice', parseFloat(e.target.value) || 0)}
                                  className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none" data-testid={`edit-room-base-price-${index}`} />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Weekend Price</label>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">&#8377;</span>
                                <input type="number" min={0} value={room.weekendPrice ?? ''} onChange={(e) => updateRoom(room.id, 'weekendPrice', e.target.value)} placeholder="Amount"
                                  className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none" data-testid={`edit-room-weekend-price-${index}`} />
                              </div>
                            </div>
                          </div>
                          {/* Extra Adult Price & Occupancy */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Extra Adult Price</label>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">&#8377;</span>
                                <input type="number" min={0} value={room.extraAdultPrice ?? ''} onChange={(e) => updateRoom(room.id, 'extraAdultPrice', e.target.value)} placeholder="Amount"
                                  className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none" data-testid={`edit-room-extra-adult-price-${index}`} />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Occupancy</label>
                              <input type="number" min={1} max={20} value={occ} onChange={(e) => updateRoom(room.id, 'occupancy', parseInt(e.target.value) || 1)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none" data-testid={`edit-room-occupancy-${index}`} />
                            </div>
                          </div>
                          {/* Adults & Children */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">No of Adults</label>
                              <input type="number" min={1} max={20} value={room.noOfAdults ?? ''} onChange={(e) => updateRoom(room.id, 'noOfAdults', e.target.value)} placeholder="e.g. 2"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none" data-testid={`edit-room-adults-${index}`} />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">No of Children</label>
                              <input type="number" min={0} max={20} value={room.noOfChildren ?? ''} onChange={(e) => updateRoom(room.id, 'noOfChildren', e.target.value)} placeholder="e.g. 1"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none" data-testid={`edit-room-children-${index}`} />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    {/* Card Footer */}
                    <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                      <span>{room.count || 0} rooms &middot; {room.roomTypeCategory || 'AC'}</span>
                      <span className="font-semibold text-gray-700">&#8377;{room.basePrice?.toLocaleString('en-IN') || 0}/night</span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {formData.rooms.length > 0 && (
              <div className="bg-gray-800 text-white p-4 rounded-xl flex justify-between items-center text-sm" data-testid="room-summary-bar">
                <span>Total Room Types: <strong>{formData.rooms.length}</strong></span>
                <span>Total Inventory: <strong>{formData.rooms.reduce((sum, r) => sum + (r.count || 0), 0)}</strong> rooms</span>
              </div>
            )}
          </div>
        )}
      </FormSection>

      {/* 4. Amenities & Facilities */}
      <FormSection title="Amenities & Facilities" icon={Wifi} defaultOpen={mode !== 'view'} color="orange">
        {/* NOTE: The legacy property-level `amenities` field is intentionally NOT
            rendered here. Room-specific amenities (Wifi/AC/Fan/Bathroom/etc.)
            are shown per-room inside each Room Inventory card (see line ~1049).
            Property-level policies live below in "Property Facilities & Policies".
            The legacy top-level `amenities` field is preserved in Mongo but not
            displayed to avoid duplicating room amenities as a misleading
            property-wide list (see Riverview Haven investigation, 2026-02-11). */}
        {/* Room Amenities — editable per room type (mirrors Master Form Step 4) */}
        {mode !== 'view' && (
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-1">Room Amenities</h4>
            <p className="text-sm text-gray-500 mb-4">Select amenities for each room category.</p>
            {(formData.rooms || []).length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center bg-gray-50 rounded-lg">Add room categories in Room Inventory to configure amenities.</p>
            ) : (
              <div className="space-y-5">
                {formData.rooms.map((room, idx) => {
                  const roomLabel = room.category === 'Other' ? (room.customRoomType || 'Custom Room') : room.category;
                  const amenityList = [...ROOM_AMENITIES, ...Object.keys(room.amenities || {}).filter(k => !ROOM_AMENITIES.includes(k))];
                  return (
                    <div key={room.id} className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`edit-room-amenities-card-${idx}`}>
                      <div className="px-4 py-2.5 bg-gradient-to-r from-slate-800 to-slate-700">
                        <span className="text-white font-semibold text-sm">{roomLabel} <span className="text-slate-300 font-normal">&middot; {room.roomTypeCategory || 'AC'}</span></span>
                      </div>
                      <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-2.5">
                        {amenityList.map(amenity => renderRoomAmenityChip(room, amenity))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <div>
          <h4 className="text-lg font-semibold text-gray-800 mb-4">Property Facilities &amp; Policies</h4>
          {mode === 'view' ? (
            // View Mode: Show ONLY facilities with truthy values (filter out false/null)
            (() => {
              const selected = Object.entries(formData.facilities || {}).filter(([_, v]) => v && v !== false);
              return selected.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {selected.map(([facility, config]) => (
                    <div
                      key={facility}
                      className="px-4 py-3 rounded-xl border-2 border-teal-500 bg-teal-50/30"
                      data-testid={`view-facility-${facility}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded flex items-center justify-center bg-teal-500 text-white">
                          <Check size={14} strokeWidth={3} />
                        </div>
                        <span className="text-sm font-medium text-gray-900">{facility}</span>
                      </div>
                      <FacilityBadges config={config} name={facility} />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 py-4 text-center bg-gray-50 rounded-lg">No property facilities selected</p>
              );
            })()
          ) : (
            // Edit/Create Mode: full Master Form facility groups + legacy extras
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-3 mb-2">Policies (Yes / No)</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {YES_NO_FACILITIES.map(name => renderYesNoFacility(name))}
              </div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-5 mb-2">Chargeable Facilities</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {CHARGE_FACILITIES.map(name => renderChargeFacility(name))}
              </div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-5 mb-2">Other Facilities</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {TOGGLE_FACILITIES.map(name => renderToggleFacility(name))}
                {extraFacilityKeys.map(facility => renderLegacyYesNoFacility(facility))}
              </div>
            </div>
          )}
        </div>

        {/* Other Amenities / Facilities (free text) */}
        <div className="mt-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Other Amenities / Facilities</h4>
          {mode === 'view' ? (
            <p className="text-sm text-gray-700 whitespace-pre-wrap" data-testid="property-other-facilities-view">
              {formData.other_facilities?.trim() ? formData.other_facilities : <span className="text-gray-400">Not provided</span>}
            </p>
          ) : (
            <textarea
              rows={3}
              value={formData.other_facilities || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, other_facilities: e.target.value }))}
              placeholder="e.g. Lift, EV Charging, Garden, Terrace, Rooftop Cafe"
              disabled={isDisabled}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none resize-y disabled:bg-gray-50"
              data-testid="property-other-facilities-input"
            />
          )}
        </div>
      </FormSection>
      <FormSection title="Banking Details" icon={CreditCard} defaultOpen={mode !== 'view'} color="green">
        <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-xl mb-5">
          <p className="text-sm text-emerald-700">
            Banking details are used for payouts. Cheque & QR uploads are optional.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {renderField(
            'Account Holder Name',
            <InputField
              label="Account Holder Name"
              value={formData.banking?.accountHolderName}
              onChange={(e) => handleBankingChange('accountHolderName', e.target.value)}
              placeholder="As per bank records"
              disabled={isDisabled}
            />,
            formData.banking?.accountHolderName
          )}

          {renderField(
            'Bank Name',
            <InputField
              label="Bank Name"
              value={formData.banking?.bankName}
              onChange={(e) => handleBankingChange('bankName', e.target.value)}
              placeholder="e.g. HDFC Bank"
              disabled={isDisabled}
            />,
            formData.banking?.bankName
          )}
          
          {renderField(
            'Account Number',
            <InputField
              label="Account Number"
              value={formData.banking?.accountNumber}
              onChange={(e) => handleBankingChange('accountNumber', e.target.value)}
              placeholder="XXXXXXXXXXXX"
              disabled={isDisabled}
            />,
            formData.banking?.accountNumber || ''
          )}
          
          {renderField(
            'IFSC Code',
            <InputField
              label="IFSC Code"
              value={formData.banking?.ifscCode}
              onChange={(e) => handleBankingChange('ifscCode', e.target.value.toUpperCase())}
              placeholder="e.g. HDFC0001234"
              maxLength={11}
              disabled={isDisabled}
            />,
            formData.banking?.ifscCode
          )}

          {renderField(
            'Branch Name',
            <InputField
              label="Branch Name"
              value={formData.banking?.branchName}
              onChange={(e) => handleBankingChange('branchName', e.target.value)}
              placeholder="e.g. MG Road Branch"
              disabled={isDisabled}
            />,
            formData.banking?.branchName
          )}

          {renderField(
            'UPI ID',
            <InputField
              label="UPI ID"
              value={formData.banking?.upiId}
              onChange={(e) => handleBankingChange('upiId', e.target.value)}
              placeholder="example@upi"
              disabled={isDisabled}
            />,
            formData.banking?.upiId
          )}
        </div>

        {/* Cancelled Cheque & QR uploads — Edit/Create mode.
            View mode falls through to the read-only preview block below. */}
        {(mode === 'create' || mode === 'edit') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            {/* Cancelled Cheque */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Cancelled Cheque <span className="text-gray-400">(optional)</span>
              </label>
              {formData.banking?.cancelledChequeUrl ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText size={18} className="text-red-500 shrink-0" />
                      <span className="text-sm text-gray-700 truncate">
                        {formData.banking?.cancelledChequeName || 'Cheque'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <label
                        className="p-1.5 text-teal-600 hover:bg-teal-50 rounded-lg cursor-pointer"
                        title="Replace cheque"
                        data-testid="edit-cheque-replace-label"
                      >
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.webp"
                          className="hidden"
                          disabled={bankUploading.cheque || isDisabled}
                          onChange={(e) => handleBankDocUpload('cheque', e.target.files[0])}
                          data-testid="edit-cheque-replace-input"
                        />
                        <Upload size={16} />
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          handleBankingChange('cancelledChequeUrl', '');
                          handleBankingChange('cancelledChequeName', '');
                        }}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                        data-testid="edit-cheque-remove"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                  {/\.(jpg|jpeg|png|webp)$/i.test(formData.banking.cancelledChequeUrl) ? (
                    <a
                      href={`${API_BASE}${formData.banking.cancelledChequeUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open full size"
                    >
                      <img
                        src={`${API_BASE}${formData.banking.cancelledChequeUrl}`}
                        alt="Cancelled cheque preview"
                        className="w-full max-h-72 object-contain rounded-md border border-gray-200 bg-white"
                        data-testid="edit-cheque-preview"
                      />
                    </a>
                  ) : (
                    <a
                      href={`${API_BASE}${formData.banking.cancelledChequeUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-teal-600 hover:underline"
                    >
                      Open cheque (PDF)
                    </a>
                  )}
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-teal-400 hover:bg-teal-50/50 transition-colors">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    className="hidden"
                    disabled={bankUploading.cheque || isDisabled}
                    onChange={(e) => handleBankDocUpload('cheque', e.target.files[0])}
                    data-testid="edit-cheque-upload"
                  />
                  {bankUploading.cheque ? (
                    <Loader2 size={18} className="animate-spin text-teal-600" />
                  ) : (
                    <Upload size={18} className="text-gray-400" />
                  )}
                  <span className="text-sm text-gray-500">
                    {bankUploading.cheque ? 'Uploading...' : 'Upload cheque (PDF/Image, Max 10MB)'}
                  </span>
                </label>
              )}
            </div>

            {/* QR Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Payment QR Code <span className="text-gray-400">(optional)</span>
              </label>
              {formData.banking?.qrCodeUrl ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <QrCode size={18} className="text-teal-600 shrink-0" />
                      <span className="text-sm text-gray-700 truncate">
                        {formData.banking?.qrCodeName || 'QR Code'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <label
                        className="p-1.5 text-teal-600 hover:bg-teal-50 rounded-lg cursor-pointer"
                        title="Replace QR"
                        data-testid="edit-qr-replace-label"
                      >
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp"
                          className="hidden"
                          disabled={bankUploading.qr || isDisabled}
                          onChange={(e) => handleBankDocUpload('qr', e.target.files[0])}
                          data-testid="edit-qr-replace-input"
                        />
                        <Upload size={16} />
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          handleBankingChange('qrCodeUrl', '');
                          handleBankingChange('qrCodeName', '');
                        }}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                        data-testid="edit-qr-remove"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                  <a
                    href={`${API_BASE}${formData.banking.qrCodeUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open full size"
                  >
                    <img
                      src={`${API_BASE}${formData.banking.qrCodeUrl}`}
                      alt="Payment QR code preview"
                      className="mx-auto w-56 h-56 object-contain rounded-md border border-gray-200 bg-white p-2"
                      data-testid="edit-qr-preview"
                    />
                  </a>
                  <p className="text-[11px] text-gray-400 text-center">
                    Tap the QR to open it full size for scanning.
                  </p>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-teal-400 hover:bg-teal-50/50 transition-colors">
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp"
                    className="hidden"
                    disabled={bankUploading.qr || isDisabled}
                    onChange={(e) => handleBankDocUpload('qr', e.target.files[0])}
                    data-testid="edit-qr-upload"
                  />
                  {bankUploading.qr ? (
                    <Loader2 size={18} className="animate-spin text-teal-600" />
                  ) : (
                    <Upload size={18} className="text-gray-400" />
                  )}
                  <span className="text-sm text-gray-500">
                    {bankUploading.qr ? 'Uploading...' : 'Upload QR (JPG/PNG/WEBP, Max 10MB)'}
                  </span>
                </label>
              )}
            </div>
          </div>
        )}

        {/* Cancelled Cheque & QR — view (3D: large, clear, scannable previews) */}
        {mode === 'view' && (formData.banking?.cancelledChequeUrl || formData.banking?.qrCodeUrl) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            {formData.banking?.cancelledChequeUrl && (
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Cancelled Cheque</label>
                {/\.(jpg|jpeg|png|webp)$/i.test(formData.banking.cancelledChequeUrl) ? (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${formData.banking.cancelledChequeUrl}`} target="_blank" rel="noreferrer" title="Open full size" data-testid="property-cheque-link">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${formData.banking.cancelledChequeUrl}`} alt="Cancelled cheque"
                      className="w-full max-h-72 object-contain border border-gray-200 rounded-lg bg-white" data-testid="property-cheque-preview" />
                  </a>
                ) : (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${formData.banking.cancelledChequeUrl}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-teal-600 hover:underline" data-testid="property-cheque-link">
                    <FileText size={16} /> {formData.banking.cancelledChequeName || 'View Cheque (PDF)'}
                  </a>
                )}
              </div>
            )}
            {formData.banking?.qrCodeUrl && (
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Payment QR Code</label>
                <a href={`${process.env.REACT_APP_BACKEND_URL}${formData.banking.qrCodeUrl}`} target="_blank" rel="noreferrer" title="Open full size for scanning" data-testid="property-qr-link">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${formData.banking.qrCodeUrl}`} alt="Payment QR" className="w-56 h-56 object-contain border border-gray-200 rounded-lg p-2 bg-white" data-testid="property-qr-preview" />
                </a>
                <p className="text-[11px] text-gray-400 mt-1">Tap the QR to open it full size for scanning.</p>
              </div>
            )}
          </div>
        )}
      </FormSection>

      {/* Form Actions */}
      {(mode === 'create' || mode === 'edit') && (
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          {!isPublic && (
            <button
              type="button"
              onClick={onCancel}
              className="px-5 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium flex items-center gap-2 disabled:opacity-50"
            data-testid="save-property-btn"
          >
            {saving ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {isPublic ? 'Submitting...' : 'Saving...'}
              </>
            ) : (
              <>
                <Save size={18} />
                {isPublic ? 'Submit Property' : (mode === 'create' ? 'Create Property' : 'Save Changes')}
              </>
            )}
          </button>
        </div>
      )}

      {mode === 'view' && (
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
          >
            Back to Properties
          </button>
          {!restrictedFromEdit && (
            <button
              type="button"
              onClick={() => onModeChange?.('edit')}
              className="px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium flex items-center gap-2"
              data-testid="edit-property-btn"
            >
              Edit Property
            </button>
          )}
        </div>
      )}
    </form>
  );
};

export default HotelOnboardingForm;
